// Fotos dentro de un PDF de jsPDF.
//
// jsPDF necesita los bytes de la imagen, no una URL: hay que bajarla y convertirla.
// Y hay que REDIMENSIONARLA antes de incrustarla — una foto de celular de 3MB por fila
// genera un PDF de decenas de MB que no se puede mandar por WhatsApp. Las bajamos a
// ~160px de lado, que es de sobra para una miniatura impresa de 18mm.
//
// Todo falla en silencio a propósito: si una foto no carga, el PDF se genera igual sin
// esa miniatura. Un pedido al proveedor no se puede caer porque una imagen esté rota.

export interface PdfImage { dataUrl: string; w: number; h: number }

const MAX_PX = 160;

async function toDataUrl(url: string, maxPx = MAX_PX): Promise<PdfImage | null> {
    try {
        // El bucket es público y tiene CORS, así que el fetch desde el browser anda.
        const res = await fetch(url, { mode: 'cors' });
        if (!res.ok) return null;
        const blob = await res.blob();
        const bmp = await createImageBitmap(blob);
        const scale = Math.min(1, maxPx / Math.max(bmp.width, bmp.height));
        const w = Math.max(1, Math.round(bmp.width * scale));
        const h = Math.max(1, Math.round(bmp.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        // Fondo blanco: un PNG con transparencia queda negro al pasar a JPEG.
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(bmp, 0, 0, w, h);
        bmp.close?.();
        // JPEG calidad 0.8: ~6KB por miniatura contra ~40KB en PNG.
        return { dataUrl: canvas.toDataURL('image/jpeg', 0.8), w, h };
    } catch {
        return null;
    }
}

/** Baja y prepara varias imágenes en paralelo. Devuelve un mapa url → imagen lista.
 *  `maxPx`: lado máximo. 160 alcanza para una miniatura de 18mm; para una foto grande
 *  (ej. 42mm en el pedido a proveedor) usar ~480 o se ve pixelada al imprimir. */
export async function loadPdfImages(urls: (string | null | undefined)[], maxPx = MAX_PX): Promise<Map<string, PdfImage>> {
    const unique = Array.from(new Set(urls.filter((u): u is string => !!u)));
    const out = new Map<string, PdfImage>();
    await Promise.all(unique.map(async (u) => {
        const img = await toDataUrl(u, maxPx);
        if (img) out.set(u, img);
    }));
    return out;
}

/**
 * Dibuja una imagen centrada dentro de una celda de autoTable, respetando su proporción.
 * Se llama desde el hook `didDrawCell`.
 */
export function drawImageInCell(
    doc: any, img: PdfImage, cell: { x: number; y: number; width: number; height: number }, padding = 1.5,
) {
    const boxW = cell.width - padding * 2;
    const boxH = cell.height - padding * 2;
    if (boxW <= 0 || boxH <= 0) return;
    const ratio = Math.min(boxW / img.w, boxH / img.h);
    const w = img.w * ratio;
    const h = img.h * ratio;
    doc.addImage(
        img.dataUrl, 'JPEG',
        cell.x + (cell.width - w) / 2,
        cell.y + (cell.height - h) / 2,
        w, h,
    );
}
